import { useState } from "react";
import { AdvicelineNurseLayout } from "@/components/layout/adviceline-nurse-layout";
import { NurseLayout } from "@/components/layout/nurse-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "wouter";
import { ArrowLeft, CheckCircle2, Loader2, AlertTriangle, Users } from "lucide-react";

export default function JoinTeam() {
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdvicelineHub = typeof window !== "undefined" && window.location.hostname.includes("advicelinehub");
  const Layout = isAdvicelineHub ? AdvicelineNurseLayout : NurseLayout;

  const handleJoin = async () => {
    if (!code.trim()) return;
    setJoining(true);
    setError(null);

    try {
      const res = await fetch("/api/teams/join", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim() }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed to join" }));
        setError(err.error ?? "Failed to join team");
        return;
      }

      setJoined(true);
    } catch {
      setError("Network error — please try again");
    } finally {
      setJoining(false);
    }
  };

  if (joined) {
    return (
      <Layout title="Join Team">
        <div className="max-w-md mx-auto text-center space-y-6 py-16">
          <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-6">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-900 mb-1">Welcome to the team!</h2>
            <p className="text-sm text-slate-600">You've successfully joined. Head to your dashboard to start managing requests.</p>
          </div>
          <div className="flex gap-3 justify-center">
            <Link href="/nurse">
              <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700">Go to requests</Button>
            </Link>
            <Link href="/nurse/team">
              <Button variant="outline" className="rounded-full">Team management</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Join Team">
      <div className="max-w-md mx-auto space-y-6">
        <Link href="/nurse" className="text-slate-500 hover:text-slate-900 text-sm flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-indigo-100 p-2 rounded-xl">
              <Users className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Join a Team</h2>
              <p className="text-sm text-slate-500">Enter the code provided by your Team Lead or administrator</p>
            </div>
          </div>

          <div>
            <Label htmlFor="code" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Join code
            </Label>
            <Input
              id="code"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. ALH-RHEUM-7XK2"
              className="rounded-xl font-mono text-lg tracking-wider text-center"
              autoFocus
              onKeyDown={e => e.key === "Enter" && handleJoin()}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          <Button
            onClick={handleJoin}
            disabled={!code.trim() || joining}
            className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
          >
            {joining ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Joining…</> : "Join team"}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
